import { defineComponent } from "vue";
import { Container } from "aurelia-dependency-injection";
import { required } from "@vuelidate/validators";
import useVuelidate from "@vuelidate/core";

import { AssetsService } from "../../../services/assets-service";
import { ToastrService } from "../../../services/toastr.service";
import { DocumentTypeCategory, ParentPermissionEnum, PermissionActionEnum } from "../../../enums/enums";
import { IGetDocumentTypeLean } from "../../../resources-vue/vue-interfaces/i-document-type";
import { ITypeaheadOptions } from "../../vue-interfaces/i-typeahead";
import { IReferralDocument } from "../../vue-interfaces/i-get-referral";
import { IPatientFileDetails } from "../../../resources-vue/vue-interfaces/i-get-patient";
import { ReferralService } from "../../../services/referral-service";
import { IPromptOptions } from "../../vue-dialogs/prompt/prompt";
import { formatDate } from "../../../common/vue-helpers/modifiers/value-modifier";
import { PermissionManager } from "../../../common/utilities/permission-manager";

import TableEmpty from "../TableEmpty/TableEmpty.vue";
import Prompt from "../../vue-dialogs/prompt/prompt.vue";
import TypeaheadInput from "../TypeaheadInput/TypeaheadInput.vue";

export default defineComponent({
    components: {
        TableEmpty,
        TypeaheadInput
    },

    props: {
        title: { type: String },
        patientId: { type: String },
        accountId: { type: String },
        application: { type: Number },
        uploadDocument: { type: Function },
        getDocuments: { type: Function },
        patientVersionId: { type: String },
        providerId: { type: String },
        deleteDocument: { type: Function },
        isHistoryPage: { type: Boolean, default: false }
    },

    validations() {
        return {
            fileDetails: this.fileDetails.map(() => ({
                type: { required }
            }))
        };
    },

    data() {
        return {
            _assetsService: null,
            _toastrService: null,
            _referralService: null,
            _permissionManager: null,
            uploading: false as boolean,
            loadingDocuments: false as boolean,
            patientDocuments: [] as IReferralDocument[],
            addPatientDocumentsPermission: false as boolean,
            searchedTypes: [],
            selectedUploadType: null as ITypeaheadOptions,
            DocumentTypeCategoryEnum: DocumentTypeCategory,
            deletePatientDocumentsPermission: false as boolean,
            fileDetails: [],
            uploadFile: [],
            moreFiles: [],
            loadingCount: 3,
            v$: useVuelidate(),
            formatDate
        };
    },

    watch: {
        uploadFile(newValue: File[]) {
            let files: File[] = Array.from(newValue);
            this.fileDetails = files.map((file: File) => ({ name: file.name, type: null, file: file }));
        },

        moreFiles(newValue: File[]) {
            let files: File[] = Array.from(newValue);
            this.fileDetails = this.fileDetails.concat(
                files.map((file: File) => ({ name: file.name, type: null, file: file }))
            );
        }
    },

    created() {
        this._assetsService = Container.instance.get(AssetsService);
        this._toastrService = Container.instance.get(ToastrService);
        this._referralService = Container.instance.get(ReferralService);
        this._permissionManager = Container.instance.get(PermissionManager);
    },

    async mounted() {
        await this.loadSearchedTypes();
        this.addPatientDocumentsPermission = this._permissionManager.checkPermission(
            ParentPermissionEnum.documents,
            PermissionActionEnum.canAdd,
            this.accountId
        );
        this.deletePatientDocumentsPermission = this._permissionManager.checkPermission(
            ParentPermissionEnum.documents,
            PermissionActionEnum.canDelete,
            this.accountId
        );
        this.loadFilesList();
    },

    methods: {
        async loadSearchedTypes() {
            if (!this.patientId) {
                this.searchedTypes = [];
            }
            let category = Number(this.DocumentTypeCategoryEnum.enum.Patient);
            let searchedTypes: IGetDocumentTypeLean[] = await this._referralService.getDocumentTypesList({
                providerIds: [this.providerId],
                application: this.application,
                accountId: this.accountId,
                category
            });
            if (searchedTypes) {
                this.searchedTypes = searchedTypes.map((documentType: IGetDocumentTypeLean) => ({
                    value: documentType.id,
                    name: documentType.type
                }));
            } else {
                this.searchedTypes = [];
            }
        },

        async loadFilesList() {
            if (this.patientId) {
                this.loadingDocuments = true;
                this.patientDocuments = await this.getDocuments(this.patientId);
                this.loadingDocuments = false;
            }
        },

        uploadFilesChanged(event: any) {
            this.uploadFile = event.target.files;
        },

        moreFilesAdded(event: any) {
            this.moreFiles = event.target.files;
        },

        moreFilesChanged(newValue: File[], oldValue: File[]) {
            let files: File[] = Array.from(newValue);
            this.fileDetails = this.fileDetails.concat(
                files.map((file: File) => ({ name: file.name, type: null, file: file }))
            );
        },

        uploadTypeChanged(newValue: ITypeaheadOptions, index: any) {
            if (newValue) {
                this.fileDetails[index].type = newValue.value;
            }
        },

        removeFile(index: number) {
            this.fileDetails = this.fileDetails.filter((file, indx) => index != indx);
        },

        getFileIcon(type: string) {
            if (!type) {
                return "fa-file";
            }
            if (type.match("image.*")) {
                return "fa-file-image";
            } else if (type.includes("wordprocessing")) {
                return "fa-file-word";
            } else if (type.includes("spreadsheet")) {
                return "fa-file-excel";
            } else if (type.includes("pdf")) {
                return "fa-file-pdf";
            } else {
                return "fa-file";
            }
        },

        async deleteSelectedDocument(documentId: string) {
            try {
                let promptOptions: IPromptOptions = {
                    message: "Are you sure you want to delete?",
                    okText: "Yes",
                    cancelText: "No"
                };

                this.$dialog.open(Prompt, {
                    props: {
                        modal: true,
                        showHeader: false
                    },
                    data: { promptOptions },
                    onClose: async (confirm) => {
                        if (confirm.data) {
                            await this.deleteDocument(this.patientId, documentId);
                            this.loadFilesList();
                            this._toastrService.success({
                                message: `The document has been successfully deleted.`,
                                title: `Document Deleted`
                            });
                        }
                    }
                });
            } catch (error) {
                this._toastrService.error({
                    title: `Error - Document`,
                    message: `There was a problem while deleting the document. Please try again.`
                });
            }
        },

        async uploadDocuments() {
            const validated = await this.v$.$validate();
            if (validated) {
                try {
                    this.uploading = true;
                    await this.asyncForEach(this.fileDetails, async (fileDetail) => {
                        if (fileDetail?.type) {
                            let assetId = await this._assetsService.uploadFile(
                                fileDetail.file,
                                this.accountId,
                                this.application
                            );
                            await this.uploadDocument(this.patientId, {
                                assetId: assetId,
                                documentName: fileDetail.name,
                                uploadType: fileDetail.type,
                                patientVersionId: this.patientVersionId,
                                application: this.application,
                                accountId: this.accountId
                            });
                        } else {
                            throw new Error("No Type");
                        }
                    });
                    this.fileDetails = [];
                    this.loadFilesList();
                    this._toastrService.success({
                        title: `Upload Successful`,
                        message: `The files have been successfully uploaded`
                    });
                } catch (error) {
                    this._toastrService.error({
                        title: `Error`,
                        message: `There was a problem while uploading files. Please try again.`
                    });
                }
                this.uploading = false;
            } else {
                this._toastrService.error({
                    title: `Error`,
                    message: `Please fill required fields.`
                });
            }
        },

        async asyncForEach(array: IPatientFileDetails[], callback: (item: IPatientFileDetails) => void) {
            for (let index = 0; index < array.length; index++) {
                await callback(array[index]);
            }
        },

        isFieldRequired(index: number) {
            return this.v$.$errors.find((error) =>
                error.$propertyPath === `fileDetails.${index}.type` ? true : false
            );
        },
        removeAllFiles() {
            this.fileDetails = [];
        }
    }
});
